import React from 'react';
import PropTypes from 'prop-types';
import {
    Box, Divider, InputAdornment, Stack, Switch, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ConfigCard from './ConfigCard';

// Visual position-picker tiles. Each value is the storefront-side identifier
// the gallery component reads to lay out the thumbnails. "hidden" turns the
// thumbnail strip off entirely.
const POSITIONS = [
    { value: 'left',   i18nKey: 'cfg_gallery_pos_left',   fallback: 'Left'   },
    { value: 'right',  i18nKey: 'cfg_gallery_pos_right',  fallback: 'Right'  },
    { value: 'above',  i18nKey: 'cfg_gallery_pos_above',  fallback: 'Above'  },
    { value: 'below',  i18nKey: 'cfg_gallery_pos_below',  fallback: 'Below'  },
    { value: 'hidden', i18nKey: 'cfg_gallery_pos_hidden', fallback: 'Hidden' },
];

// Tiny pictogram per position so admins immediately see what they're picking.
// Pure CSS — no extra icon imports — keeps the bundle lean.
const PositionTile = ({ value }) => {
    const main  = { width: 22, height: 22, backgroundColor: 'currentColor', borderRadius: 2, opacity: 0.9 };
    const thumb = { width: 4,  height: 4,  backgroundColor: 'currentColor', borderRadius: 0.5, opacity: 0.6 };
    if (value === 'hidden') return <Box sx={main} />;
    if (value === 'above' || value === 'below') {
        const thumbs = (
            <Box sx={{ display: 'flex', gap: 0.4 }}>
                {[0,1,2].map(i => <Box key={i} sx={{ ...thumb, width: 6, height: 4 }} />)}
            </Box>
        );
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
                {value === 'above' && thumbs}
                <Box sx={main} />
                {value === 'below' && thumbs}
            </Box>
        );
    }
    // left / right
    const thumbsCol = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {[0,1,2].map(i => <Box key={i} sx={{ ...thumb, width: 4, height: 6 }} />)}
        </Box>
    );
    return (
        <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
            {value === 'left' && thumbsCol}
            <Box sx={main} />
            {value === 'right' && thumbsCol}
        </Box>
    );
};

/**
 * "Image gallery" card — controls how product images are laid out on the
 * detail page. Thumbnail position tiles, grid-vs-scroll mode, max thumbnails,
 * and arrow toggles with their per-element colours.
 *
 * value shape:
 *   { thumbnailPosition: 'left' | 'right' | 'above' | 'below' | 'hidden',
 *     useGrid:        bool,
 *     maxThumbnails:  number,
 *     showArrows:     bool,
 *     arrowColor:     string,
 *     arrowBgColor:   string,
 *     showFullscreen: bool,
 *     fullscreenArrowColor:   string,
 *     fullscreenArrowBgColor: string }
 */
const ImageGalleryCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const useGrid       = get('useGrid', true);
    const showArrows    = get('showArrows', true);
    const showFullscreen = get('showFullscreen', true);

    return (
        <ConfigCard
            title={tr('cfg_gallery_title', 'Image gallery')}
            description={tr('cfg_gallery_desc', 'How product images appear on the detail page.')}
            theme={theme}
        >
            {/* Thumbnail position — visual tile picker */}
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {tr('cfg_gallery_pos_title', 'Thumbnail position')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    {tr('cfg_gallery_pos_desc', 'Where the small image thumbnails appear, relative to the main product image.')}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={get('thumbnailPosition', 'left')}
                    onChange={(_e, v) => v && set('thumbnailPosition')(v)}
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, '& .MuiToggleButton-root': { borderRadius: '8px !important', border: '1px solid rgba(0,0,0,0.12) !important' } }}
                >
                    {POSITIONS.map((p) => (
                        <ToggleButton key={p.value} value={p.value} sx={{ flexDirection: 'column', gap: 0.5, px: 1.5, py: 1, minWidth: 72, textTransform: 'none' }}>
                            <PositionTile value={p.value} />
                            <Typography variant="caption">{tr(p.i18nKey, p.fallback)}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            <Divider />

            {/* Grid vs scroll */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr('cfg_gallery_use_grid', 'Use a grid-based gallery')}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {tr('cfg_gallery_use_grid_desc', 'Shows a fixed number of thumbnails in a grid and supports playing videos. Turn off for a simple scrollable list.')}
                        </Typography>
                    </Box>
                    <Switch
                        color="success"
                        size="small"
                        checked={!!useGrid}
                        onChange={(e) => set('useGrid')(e.target.checked)}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>
                {useGrid && (
                    <Box sx={{ mt: 1.5 }}>
                        <TextField
                            type="number"
                            size="small"
                            label={tr('cfg_gallery_max_thumbs', 'Maximum thumbnails to show')}
                            value={get('maxThumbnails', 3) ?? 3}
                            onChange={(e) => set('maxThumbnails')(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_gallery_thumb_unit', 'thumbnails')}</InputAdornment> }}
                            helperText={tr('cfg_gallery_max_thumbs_helper', 'If a product has more images than this, the rest are tucked away behind a "view more" action.')}
                            sx={{ maxWidth: 320 }}
                        />
                    </Box>
                )}
            </Box>

            <Divider />

            {/* Arrows on the main image */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr('cfg_gallery_arrows', 'Show arrows on the main image')}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {tr('cfg_gallery_arrows_desc', 'Lets visitors flip through images by clicking left/right arrows on the main image.')}
                        </Typography>
                    </Box>
                    <Switch
                        color="success"
                        size="small"
                        checked={!!showArrows}
                        onChange={(e) => set('showArrows')(e.target.checked)}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>
                {showArrows && (
                    <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                        <ColorMini label={tr('cfg_gallery_arrow_color', 'Arrow color')} value={get('arrowColor', '#000000')} onChange={set('arrowColor')} />
                        <ColorMini label={tr('cfg_gallery_arrow_bg', 'Background color')} value={get('arrowBgColor', '#ffffff')} onChange={set('arrowBgColor')} />
                    </Stack>
                )}
            </Box>

            <Divider />

            {/* Fullscreen arrow */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr('cfg_gallery_fullscreen', "Show 'open fullscreen' arrow below the thumbnails")}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {tr('cfg_gallery_fullscreen_desc', 'Adds an arrow below the thumbnails that opens a fullscreen preview of the current image.')}
                        </Typography>
                    </Box>
                    <Switch
                        color="success"
                        size="small"
                        checked={!!showFullscreen}
                        onChange={(e) => set('showFullscreen')(e.target.checked)}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>
                {showFullscreen && (
                    <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                        <ColorMini label={tr('cfg_gallery_arrow_color', 'Arrow color')} value={get('fullscreenArrowColor', '#000000')} onChange={set('fullscreenArrowColor')} />
                        <ColorMini label={tr('cfg_gallery_arrow_bg', 'Background color')} value={get('fullscreenArrowBgColor', '#ffffff')} onChange={set('fullscreenArrowBgColor')} />
                    </Stack>
                )}
            </Box>
        </ConfigCard>
    );
};

// Compact colour-swatch input — small enough to sit inline next to the toggle
// labels without crowding the card. Uses native `<input type="color">` like
// the rest of the style tab.
const ColorMini = ({ label, value, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
            component="label"
            sx={{
                position: 'relative', width: 28, height: 28, borderRadius: 1,
                border: '1px solid rgba(0,0,0,0.18)', backgroundColor: value,
                cursor: 'pointer', flexShrink: 0,
            }}
        >
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
);

ImageGalleryCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default ImageGalleryCard;
